import { makeAutoObservable } from 'mobx';
import React from 'react';
import { apiFetch } from 'framework/api';
import { getTagListByAccount, getUserTags, saveTags, TagInfo } from './api';
import { SaveTagsModel } from './tagsModel';
import { State } from 'tools';
import { ApplicationState } from 'framework/applicationState';
import { MobxManager } from 'framework/mobx-integration';
import { translator } from 'core/localization/localization';

const i = translator();

export class UserTagsStore {
	initialTagList: { label: string; value: string }[] = [];
	tagList: { label: string; value: string }[] = [];
	model: SaveTagsModel;
	showSubaccountTags: boolean = true;
	forceTeamTags: boolean = false;
	forceUserTags: boolean = false;

	mobx = new MobxManager();

	constructor() {
		makeAutoObservable(this);
		this.init();
	}

	init = async () => {
		const tagsResult = await apiFetch(getUserTags());
		if (tagsResult.success) {
			this.model = new SaveTagsModel(tagsResult.data.tags, tagsResult.data.showUntagged, tagsResult.data.ignoreTags);

			ApplicationState.showUntagged = this.model.showUntagged ?? false;
			ApplicationState.ignoreTags = this.model.ignoreTags ?? false;
			ApplicationState.userTags = this.model.tags ?? [];

			this.forceTeamTags = tagsResult.data.forceTeamTags ?? false;
			this.forceUserTags = tagsResult.data.forceUserTags ?? false;
		}

		this.mobx.reaction(
			() => this.showSubaccountTags,
			async () => {
				const tagList = await apiFetch(getTagListByAccount({ includeSubaccounts: this.showSubaccountTags }));
				if (tagList.success) {
					this.tagList = this.processUniqueValues(tagList.data);
				}
				this.model.tags = this.model.tags.filter((x) => this.tagList.some((y) => y.value == x));
			},
			{ fireImmediately: true },
		);
	};

	processUniqueValues(data: TagInfo[]): { label: string; value: string }[] {
		const valueMap = new Map<
			string,
			{
				value: string;
				accounts: { id: string; name: string }[];
				items: TagInfo[];
			}
		>();

		data.forEach((item) => {
			if (!valueMap.has(item.value)) {
				valueMap.set(item.value, {
					value: item.value,
					accounts: [],
					items: [],
				});
			}
			const group = valueMap.get(item.value);
			if (!group.accounts.find((x) => x.id === item.accountId)) {
				group.accounts.push({ name: item.accountName, id: item.accountId });
			}
			group.items.push(item);
		});

		const results: { label: string; value: string }[] = [];

		// if current account - show only value
		// if 1 subaccount - show value and account name
		// if multiple subaccounts - show value and message
		for (const [, group] of valueMap) {
			if (group.accounts.find((x) => x.id === ApplicationState.accountId)) {
				results.push({
					label: group.value,
					value: group.value,
				});
				continue;
			}

			const accountCount = group.accounts.length;

			if (accountCount === 1) {
				const account = Array.from(group.accounts)[0];
				results.push({
					label: `${group.value}(${account.name})`,
					value: group.value,
				});
			} else {
				results.push({
					label: `${group.value}(used in multiple accounts)`,
					value: group.value,
				});
			}
		}

		return results;
	}

	filterTagList(data: TagInfo[]) {
		return data?.map((x) => ({ label: x.text, value: x.value }));
		// return this.tagList.filter((x) => this.model.tags.some((y) => y == x.value));
	}

	get forcedMessage() {
		if (this.forceTeamTags && this.forceUserTags) {
			return i('These tags are forced by your team and user settings');
		}
		if (this.forceUserTags) {
			return i('These tags are forced and cannot be modified');
		}
		if (this.forceTeamTags) {
			return i('Team tags override user tags');
		}
		return null;
	}

	get mouseover() {
		if (this.forceTeamTags && this.forceUserTags) {
			return i('Team and user tags active');
		}
		if (this.forceTeamTags) {
			return i('Team tags active');
		}
		if (this.forceUserTags) {
			return i('User tags active');
		}
		return i('Tags');
	}

	get forced() {
		return this.forceTeamTags || this.forceUserTags;
	}

	get dirty(): boolean {
		return this.model?.dirty ?? false;
	}

	get active(): boolean {
		return !this.model?.ignoreTags ?? false;
	}

	get style() {
		const { clientWidth } = window.document.documentElement;
		const left = clientWidth - 300 - 20 - clientWidth / 2;
		return { top: 60, left: left };
	}

	open: boolean = false;
	onOpenChange = (open: boolean) => {
		this.open = open;
		if (this.open == false) {
			this.resetForm();
		}
	};

	onOk = async () => {
		const result = await apiFetch(saveTags(this.model));
		if (result.success) {
			ApplicationState.showUntagged = this.model.showUntagged;
			ApplicationState.ignoreTags = this.model.ignoreTags;
			ApplicationState.userTags = this.model.tags;

			if (State.currentApp.reloadByTags) {
				State.currentApp.reloadByTags();
			}
		}

		this.open = false;
	};

	onCancel = () => {
		this.open = false;
		this.resetForm();
	};

	resetForm() {
		this.model.showUntagged = ApplicationState.showUntagged;
		this.model.ignoreTags = ApplicationState.ignoreTags;
		this.model.tags = ApplicationState.userTags;
	}

	changeActive = () => {
		this.model.ignoreTags = !this.model.ignoreTags;
	};

	onChangeUntagged = (value: boolean) => {
		this.model.showUntagged = value;
	};

	onUserTagsChanged = (value: string[]) => {
		this.model.tags = value;
	};
}
export const UserTagsContext = React.createContext<UserTagsStore>(null);
